import React, { useMemo } from 'react';
import type { FamilyMember } from '../../api/families';
import { memberDisplayInitials, memberDisplayName } from '../../utils/memberDisplay';
import { avatarGradient } from './memberAvatar';
import PageBar, { PageBarMenuItem, PageBarCrumb, PageBarIconAction } from '../PageBar';

interface MemberBarProps {
  member: FamilyMember;
  canManageShare: boolean;
  canDeleteMember: boolean;
  deletingMember: boolean;
  /** When `true`, every "family" affordance lands on `/shared-members`
   *  because the viewer only has direct member-level access (not the
   *  family page). Mirrors the same prop on `MemberHero`. */
  viewedAsSharedMember: boolean;
  onUpload: () => void;
  onEdit: () => void;
  onShare: () => void;
  onDelete: () => void;
}

const IconUpload = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const IconShare = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconTrash = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

/**
 * Sticky member-bar shown above the page-container content on
 * `/members/:mid` and `/shared-members/:mid`.
 *
 * Thin composition layer over the reusable `<PageBar>` — feeds in the
 * member-flavoured crumbs (`Familii / <family> / <member name>`), the
 * initials chip, the "Încarcă" primary action and the overflow menu
 * (edit / share / view family / delete). All the layout chrome,
 * sticky positioning, scroll-shadow toggle, kebab open/close logic
 * etc. live in `PageBar`.
 */
const MemberBar: React.FC<MemberBarProps> = ({
  member,
  canManageShare,
  canDeleteMember,
  deletingMember,
  viewedAsSharedMember,
  onUpload,
  onEdit,
  onShare,
  onDelete,
}) => {
  const initials = memberDisplayInitials(member);
  const displayName = memberDisplayName(member);
  const gradient = useMemo(() => avatarGradient(member.id), [member.id]);
  const familyLabel = (member.family_name || '').trim() || 'Familie';
  const familyLinkTo = viewedAsSharedMember ? '/shared-members' : `/families/${member.family_id}`;
  const familyMenuLabel = viewedAsSharedMember ? 'Înapoi la membri partajați' : 'Vezi familia';

  // Crumb shape:
  //   own-family branch:    Dashboard › Familii › <family name> › <member>
  //   shared-member branch: Dashboard › Membri partajați › <member>
  // The shared-member branch intentionally omits the family link
  // because the viewer doesn't have family-level access — they can
  // only see the individual member that was shared with them.
  // `backTo` mirrors the second-to-last crumb so the mobile back
  // button skips the member leaf and lands one level up in the tree
  // (the family page in the own-family branch, the shared-members
  // list in the shared branch).
  const crumbs: PageBarCrumb[] = viewedAsSharedMember
    ? [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Membri partajați', to: '/shared-members' },
        { label: displayName },
      ]
    : [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Familii', to: '/families' },
        { label: familyLabel, to: `/families/${member.family_id}` },
        { label: displayName },
      ];

  const leadingChip = (
    <span className="page-bar-crumb-chip" style={{ background: gradient }} aria-hidden>
      {initials}
    </span>
  );

  // Edit + Share moved out of the kebab and onto the bar as inline
  // icon buttons (right of "Încarcă"), so the user reaches the two
  // most-common verbs in one click. "Vezi familia" stays in the
  // kebab as a navigational fallback, and Delete stays there too as
  // the rare destructive verb. PageBar auto-hides the kebab entirely
  // if `menuItems` ends up empty after these conditionals.
  const iconActions: PageBarIconAction[] = [
    {
      icon: <IconEdit />,
      label: 'Editează',
      onClick: onEdit,
    },
  ];

  if (canManageShare) {
    iconActions.push({
      icon: <IconShare />,
      label: 'Partajare',
      onClick: onShare,
    });
  }

  const menuItems: PageBarMenuItem[] = [
    {
      label: familyMenuLabel,
      icon: <IconUsers />,
      to: familyLinkTo,
    },
  ];

  if (canDeleteMember) {
    menuItems.push({
      label: 'Șterge membru',
      icon: <IconTrash />,
      onClick: onDelete,
      danger: true,
      disabled: deletingMember,
      busy: deletingMember,
      busyLabel: 'Se șterge…',
    });
  }

  return (
    <PageBar
      ariaLabel="Navigare membru"
      crumbs={crumbs}
      backTo={familyLinkTo}
      leadingChip={leadingChip}
      primaryAction={{
        label: 'Încarcă',
        icon: <IconUpload />,
        onClick: onUpload,
        ariaLabel: 'Încarcă document',
      }}
      iconActions={iconActions}
      menuItems={menuItems}
    />
  );
};

export default MemberBar;
